import { Economy } from "@highmarch/app";
import { fmtCompact } from "../kit";
import { toast } from "../toast";
import { fmtSpeedUp, flyReward } from "./ordersSheet";
import { resourceIconUrl } from "../portraits";
import { openItemPopup } from "../shops/itemPopup";
import { itemCardHtml } from "../shops/itemCard";
import { FREE_PRICE } from "../shops/shopsModel";
import { gemIcon } from "./econArt";
import { confirmPurchase, econCue, flyCurrency, purchasePop, tickPill } from "./purchaseFx";
import type { ShopDeps } from "./shopSheet";
import type { GemPrice, ShopModel } from "./shopModel";
import { purchaseFailText } from "../deals/dealsModel";
import { refusalWords } from "./refusalWords";
import { notEnoughGems } from "./getGold";

/** How long a sold card's reveal plays before the page redraws it SOLD (shop.css `.justbought`). */
const REVEAL_MS = 900;

/**
 * The Shop's purchases (FINISH_PLAN WP5): each one confirms what it buys and for how much, spends through the
 * economy (@highmarch/app Economy, hub only), then shows the receipt — the gem pill ticks down, the goods fly to their pills,
 * a toast names them. Short of gems: the button says so, and (outside the post-loss quiet) points at the gem packs.
 * Gem packs go through the Store port (PurchaseService), which grants once per transaction and saves before finishing.
 */
export class ShopActions {
  constructor(private readonly d: ShopDeps, private readonly redraw: () => void) {}

  private get eco(): Economy { return new Economy(this.d.catalog); }
  private get parent(): HTMLElement { return this.d.hub.parent; }

  bind(body: HTMLElement, m: ShopModel): void {
    body.querySelector<HTMLButtonElement>(".ebuy.b-builder")?.addEventListener("click", () => void this.buyBuilder(m));
    // owner 2026-10-24: the whole tile opens the item popup (its price button's tap bubbles to the tile)
    for (const t of body.querySelectorAll<HTMLElement>(".pack.free[data-id]")) t.onclick = () => void this.claimFree(t, m);
    for (const t of body.querySelectorAll<HTMLElement>(".pack:not(.free)[data-id]")) t.onclick = () => void this.buyPack(m, t.dataset.id!, t);
    for (const b of body.querySelectorAll<HTMLButtonElement>(".gbuy")) b.onclick = () => void this.buyGems(b.dataset.pack!, b.closest(".gpack"));
  }

  /** The store's own prices on the gem packs (StoreKit localises; the sandbox shows the reference price). */
  async fillStorePrices(body: HTMLElement): Promise<void> {
    const p = this.d.purchases;
    if (!p) { for (const b of body.querySelectorAll<HTMLButtonElement>(".gbuy")) { b.disabled = true; b.classList.add("dis"); } return; }
    const note = body.querySelector<HTMLElement>(".store-note");
    if (note && this.d.storeKind === "sandbox") note.textContent = "Test build: nothing is charged";
    for (const { pack, product } of await p.products()) {
      const label = body.querySelector<HTMLElement>(`.gbuy[data-pack="${pack.id}"] .k-amt`);
      if (label) label.textContent = product.displayPrice;
    }
  }

  /** Scrolls the gem packs into view and flashes them (the gem pill's "+", or a short purchase outside the quiet). */
  showGemPacks(root: HTMLElement): void {
    const sec = root.querySelector<HTMLElement>(".ssec.gems");
    if (!sec) return;
    sec.scrollIntoView({ block: "start", behavior: "smooth" });
    purchasePop(sec);
  }

  /** Not enough gems: say how many are missing; point at the packs unless the player just lost (ENGAGEMENT C). */
  private short(p: GemPrice, m: ShopModel): void {
    void notEnoughGems(this.parent, p.short, this.parent.querySelector(":scope > .sheet.shop"), m.nudges);
  }

  private done(before: number): void {
    this.d.hub.changed();
    tickPill("gems", before, this.eco.wallet.gems);
  }

  private async buyBuilder(m: ShopModel): Promise<void> {
    const b = m.builder;
    if (!b) return;
    if (!b.price.affordable) { this.short(b.price, m); return; }
    const ok = await confirmPurchase(this.parent, { shop: true, title: `BUILDER ${b.next}`, currency: "gems", price: b.price.gems, confirm: "HIRE",
      body: `<p><b>Yours for good.</b> ${b.next} upgrades at once, and each builder holds one more in the queue.</p>` });
    if (!ok) return;
    const before = this.eco.wallet.gems;
    const r = this.eco.buy("builder", this.d.now(), "hub");
    if (r !== true) { toast(this.parent, refusalText(r)); return; }
    econCue("buy");
    this.done(before);
    toast(this.parent, `Builder ${b.next} reports for duty! ${b.next} upgrades at once now.`);
    this.redraw();
  }

  /** Today's free gold pack: its popup (TAKE, as the Merchant's free item), then the gold flies to the pill. */
  private async claimFree(card: HTMLElement, m: ShopModel): Promise<void> {
    const p = m.resourcePacks.find((x) => x.free), gold = p?.contents ?? 0;
    if (!p?.free) return;
    const ok = await openItemPopup(this.parent, { title: "FREE GOLD", price: FREE_PRICE, confirm: "TAKE", blocked: p.free.open ? "" : "Back tomorrow", className: "c-gold",
      body: itemCardHtml({ well: { art: resourceIconUrl("gold"), count: fmtCompact(gold) }, name: "Gold", rarity: null, what: "A gift from the Merchant, once a day." }) });
    if (!ok) return;
    const r = this.eco.claimFreePack(this.d.now(), "hub");
    if (typeof r === "string") { toast(this.parent, refusalText(r)); this.redraw(); return; }
    econCue("buy");
    purchasePop(card);
    this.d.hub.changed();
    flyReward(card, r);
    toast(this.parent, receiptLine(r, 0));
    window.setTimeout(() => this.redraw(), REVEAL_MS);
  }

  private async buyPack(m: ShopModel, id: string, card: Element | null): Promise<void> {
    const p = [...m.speedUps, ...m.resourcePacks].find((x) => x.id === id && !x.free);   // the free tile shares its id
    if (!p) return;
    const speed = p.kind === "speedUp", gold = p.contents ?? 0;
    const blocked = p.opensAt !== null ? `Level ${p.opensAt}` : p.left === 0 ? "Sold out" : "";
    const ok = await openItemPopup(this.parent, { title: speed ? "SPEED-UP" : p.name?.toUpperCase() ?? "GOLD", price: `${gemIcon(20)} ${fmtCompact(p.price.gems)}`, blocked, className: "c-gems",
      body: itemCardHtml(speed ? { well: { art: resourceIconUrl("speedup"), cap: fmtSpeedUp(p.minutes) }, name: `${fmtSpeedUp(p.minutes)} speed-up`, rarity: null, what: "Cuts any upgrade short. It waits until you need it." }
        : { well: { art: resourceIconUrl(p.icon), count: gold > 0 ? fmtCompact(gold) : undefined }, name: p.name ?? "Gold", rarity: null, what: "Gold for your level, paid at once." }) });
    if (!ok) return;
    if (!p.price.affordable) { this.short(p.price, m); return; }
    const before = this.eco.wallet.gems;
    const r = this.eco.buy(id, this.d.now(), "hub");
    if (r !== true) { toast(this.parent, refusalText(r)); this.redraw(); return; }
    econCue("buy");
    purchasePop(card);
    this.done(before);
    if (p.contents) flyReward(card, p.contents);
    toast(this.parent, p.kind === "speedUp" ? `+${fmtSpeedUp(p.minutes)} speed-up: use it from any upgrade` : receiptLine(p.contents!, 0));
    window.setTimeout(() => this.redraw(), REVEAL_MS);
  }

  private async buyGems(id: string, card: Element | null): Promise<void> {
    const p = this.d.purchases;
    const pack = p?.pack(id);
    if (!p || !pack) return;
    // The platform store confirms a real payment itself; the web's test store gets one confirm so a stray tap buys nothing.
    if (this.d.storeKind === "sandbox") {
      const ok = await confirmPurchase(this.parent, { shop: true, title: "TEST PURCHASE", currency: null, price: pack.referencePrice, confirm: "BUY",
        body: `<p class="egems">${gemIcon(22)} <b>${fmtCompact(pack.gems)} gems</b></p><p>Test build: nothing is charged.</p>` });
      if (!ok) return;
    }
    const before = this.eco.wallet.gems;
    const r = await p.buy(id, "hub");
    if (r.status === "granted" || r.status === "duplicate") {
      econCue("buy");
      this.d.hub.changed();
      flyCurrency(card, "gems", before, this.eco.wallet.gems);
      if (r.status === "granted") toast(this.parent, `+${fmtCompact(r.gems)} gems. Thank you, Commander!`);
      this.redraw();
    } else if (r.status === "pending") toast(this.parent, "Awaiting approval: the gems arrive the moment it's given");
    else if (r.status === "refused" || r.status === "failed") toast(this.parent, purchaseFailText(r.reason));
  }
}

/** One toast line for a bundle: "+240 gold · +1h speed-up". */
export function receiptLine(gold: number, speedUpMinutes: number): string {
  const parts = gold > 0 ? [`+${fmtCompact(gold)} gold`] : [];
  if (speedUpMinutes > 0) parts.push(`+${fmtSpeedUp(speedUpMinutes)} speed-up`);
  return parts.join(" · ");
}

/** The economy's refusal codes (meta/shop.ts, meta/market.ts) as the player reads them (hub/refusalWords.ts). */
export function refusalText(r: string): string {
  return refusalWords(r);
}
